import * as React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  TimeFrameOptions,
} from 'pages/Dashboard/utils/trackingUtils';
import theme, { blue, cyan, pink } from 'theme';
import ContentLoader from 'components/Common/ContentLoader';
import { PieChart, Pie, Cell, Sector, ResponsiveContainer } from 'recharts';
import { MoodFactorDomain } from 'pages/Dashboard/types/waypoints.types';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import PatientDetailsCard from 'components/PatientDetailsCard';
import HLDrawer from 'components/HLDrawer';
import { TimeFrameCategory } from 'pages/Dashboard/enums';
import HLCard from 'components/HLCard';
import HLDropdown from 'components/HLDropdown';

const indexedColors = [
  cyan.Main,
  blue[700],
  blue[300],
  pink[300],
  pink.Main,
  cyan[300],
];

const renderActiveShape = (props: {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: { parameter: string, factor: string, Id: string, adjustedPercent?: number };
  percent: number;
}, onSelectMood?: (id: string, mood: string) => void) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
  } = props;
  const midOffset = 18; // distance from outerRadius to start the connector
  const angle = -RADIAN * midAngle;
  const isRight = Math.cos(angle) >= 0;
  // Start at the edge of the pie
  const connectorStartX = cx + (outerRadius + 2) * Math.cos(angle);
  const connectorStartY = cy + (outerRadius + 2) * Math.sin(angle);
  // End of angled connector
  const connectorEndX = cx + (outerRadius + midOffset) * Math.cos(angle);
  const connectorEndY = cy + (outerRadius + midOffset) * Math.sin(angle);
  // Dynamically measure text width (rough estimate: 7px per char)
  const category = (payload.parameter ?? payload.factor) || '';
  const charWidth = 7;
  const minLineLength = 40;
  // Calculate if the connector is horizontal (left or right)
  const angleDeg = Math.abs((midAngle + 360) % 360);
  const isHorizontal = angleDeg < 10 || angleDeg > 350 || (angleDeg > 170 && angleDeg < 190);
  // For single horizontal line, reduce length to avoid text cutoff
  const horizontalLineLength = Math.max(minLineLength, category.length * charWidth + 8);
  const reducedHorizontalLineLength = Math.max(
    minLineLength,
    category.length * charWidth * 0.7 + 8,
  );
  const lineLength = isHorizontal ? reducedHorizontalLineLength : horizontalLineLength;
  // Horizontal line (divider)
  const lineStartX = connectorEndX;
  const lineStartY = connectorEndY;
  const lineEndX = lineStartX + (isRight ? 1 : -1) * lineLength;
  const lineEndY = lineStartY;
  // Center for label and percent
  const labelX = lineStartX + (isRight ? 1 : -1) * (lineLength / 2);
  const labelY = lineStartY;
  // Make the vertical offset for percent match the label's offset above the line
  const percentYOffset = 6; // same as labelY - (labelY - 6)
  const textAnchor = 'middle';

  return (
    <g onClick={() => {
      onSelectMood && props?.payload?.parameter !== 'Other'
        && onSelectMood(props?.payload?.Id, props?.payload?.parameter);
    }}
    >
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      {/* Connector line from pie to divider */}
      <path d={`M${connectorStartX},${connectorStartY}L${connectorEndX},${connectorEndY}`} stroke={fill} strokeWidth={1.5} fill='none' />
      {/* Horizontal divider line, sized to fit text */}
      <path d={`M${lineStartX},${lineStartY}L${lineEndX},${lineEndY}`} stroke={fill} strokeWidth={1.5} fill='none' />
      {/* Category label above the divider */}
      <text
        x={labelX}
        y={labelY - percentYOffset}
        textAnchor={textAnchor}
        fontSize={12}
        fontWeight={400}
        fill={theme.palette.text.primary}
        alignmentBaseline='baseline'
      >
        {category}
      </text>
      {/* Percentage label just below the divider, same offset as label above */}
      <text
        x={labelX}
        y={labelY + percentYOffset}
        textAnchor={textAnchor}
        fontSize={12}
        fontWeight={700}
        fill={theme.palette.text.primary}
        alignmentBaseline='hanging'
      >
        {typeof props.payload.adjustedPercent === 'number'
          ? props.payload.adjustedPercent
          : (percent * 100).toFixed(0)}
        %
      </text>
    </g>
  );
};

function CustomPieChart({
  data,
  count,
  noData,
  isLoading,
  onSelectMood,
}: {
  data: (Metric | MoodFactorDomain)[];
  count: number;
  onSelectMood?: (moodId: string, mood: string) => void;
  noData: boolean;
  isLoading: boolean;
}): JSX.Element {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const onPieEnter = (__: {Id :string}, index: number) => {
    setActiveIndex(index);
  };

  return (
    <Box>
      <style>
        {`
          .recharts-pie-sector:focus {
            outline: none; /* Disable outline on pie slice click */
          }
        `}
      </style>

      <ResponsiveContainer width='100%' height={noData ? 0 : 240}>
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={(props) => renderActiveShape(props, onSelectMood)}
            data={data}
            cx='50%'
            cy='50%'
            innerRadius={47}
            outerRadius={65}
            fill='#27827F'
            paddingAngle={3}
            dataKey='count'
            onMouseOver={onPieEnter}
          >
            {data.map((entry, index) => {
              const actualEntry = (data[index] as Metric).parameter
              ?? (data[index] as MoodFactorDomain).factor;
              return (
                <Cell
                  key={actualEntry}
                  fill={actualEntry === 'Other' ? theme.custom.colors.lightTextSecondary : indexedColors[index % indexedColors.length]}
                  stroke={actualEntry === 'Other' ? theme.custom.colors.lightTextSecondary : indexedColors[index % indexedColors.length]}
                  strokeWidth={0.5}
                />
              );
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <ContentLoader
        isFetching={isLoading}
        isError={false}
        noDataText={noData ? `No ${onSelectMood ? 'moods' : 'factors'} exists for the selected timeframe` : ''}
        minHeight={100}
      >
        <Box display='flex' flexDirection='column' alignItems='flex-start'>
          <Box textAlign='left'>
            {data.map((metric, index) => {
              const actualEntry = (metric as Metric).parameter
             ?? (metric as MoodFactorDomain).factor;
              return Math.round((metric.count / count) * 100) > 0 ? (
                <Box
                  key={actualEntry}
                  mx={1}
                  display='flex'
                  alignItems='baseline'
                  ml={2}
                  mb={1}
                >
                  <Box
                    sx={{
                      minWidth: '8px',
                      minHeight: '8px',
                      borderRadius: '50%',
                      backgroundColor: actualEntry === 'Other' ? theme.custom.colors.lightTextSecondary : indexedColors[index % indexedColors.length],
                      marginRight: '5px',
                    }}
                  />
                  <Box>
                    <Typography variant='body1' fontWeight={700} color='text.primary'>
                      {typeof metric.adjustedPercent === 'number'
                        ? metric.adjustedPercent
                        : Math.round(((metric as Metric).count / count) * 100)}
                      %
                      {' '}
                      <Typography variant='body1' fontWeight='normal' color='text.primary' component='span'>
                        {actualEntry}
                      </Typography>
                    </Typography>
                    {(metric?.domains?.length ?? 0) > 0
                   && (
                   <Typography variant='body1' color={theme.custom.colors.lightTextSecondary}>
                     Specifically
                     {metric?.domains?.reduce((acc, d) => `${acc}, ${d.name.toLowerCase()}`, '')}
                   </Typography>
                   )}

                    {actualEntry === 'Other' && ((metric as Metric)?.parameters?.length ?? 0) > 0 && (
                    <Typography variant='body1' fontWeight='normal' color={theme.custom.colors.lightTextSecondary}>
                      {(metric as Metric).parameters}
                    </Typography>
                    )}
                    <Typography variant='body1' fontWeight='normal' color={indexedColors[index % indexedColors.length]} />
                  </Box>
                </Box>
              ) : null;
            })}
          </Box>
        </Box>
      </ContentLoader>
    </Box>
  );
}

type Metric = {
  parameter?: string;
  count: number;
  parameters?: string;
  factor?: string;
  domains?: {
    id: string;
    name: string;
  }[];
  adjustedPercent?: number;
};

function RadarChart({
  data,
  count,
}: {
  data: (Metric | MoodFactorDomain)[];
  count: number;
}): JSX.Element {
  return (
    <Grid container rowSpacing={2} flexDirection='row' justifyContent='center'>
      <Grid item xs={12} pr={data.length > 1 ? 4 : 0}>
        <Box position='relative'>
          <style>
            {`        
              .recharts-sector:focus {
                outline: none !important;
                box-shadow: none !important;
              }
            `}
          </style>
          <ResponsiveContainer width='100%' height={160}>
            <PieChart>
              <Pie
                data={data}
                cx='50%'
                cy='50%'
                innerRadius={50}
                outerRadius={80}
                fill='#27827F'
                paddingAngle={2}
                dataKey='count'
                isAnimationActive={false}
              >
                {data.map((entry, index) => {
                  const actualEntry = (data[index] as Metric).parameter
                    ?? (data[index] as MoodFactorDomain).factor;
                  return (
                    <Cell
                      key={actualEntry}
                      fill={actualEntry === 'Other' ? theme.custom.colors.lightTextSecondary : indexedColors[index % indexedColors.length]}
                      stroke={actualEntry === 'Other' ? theme.custom.colors.lightTextSecondary : indexedColors[index % indexedColors.length]}
                      strokeWidth={0.5}
                    />
                  );
                })}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
      <Grid item container rowSpacing={1.5} xs={8}>
        {data.map((_, index) => (
          Math.round((data[index].count / count) * 100) > 0 ? (
            <Grid
              key={((data[index] as Metric).parameter ?? data[index].factor)}
              item
              xs={6}
              display='flex'
              flexDirection='row'
              alignItems='baseline'
              justifyContent='flex-start'
            >
              <Box
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: ((data[index] as Metric).parameter ?? data[index].factor) === 'Other'
                    ? theme.custom.colors.lightTextSecondary
                    : indexedColors[index % indexedColors.length],
                  marginRight: '6px',
                  verticalAlign: 'middle',
                }}
              />
              <Box>
                <Typography variant='caption' fontWeight='normal' color='text.primary'>
                  {((data[index] as Metric).parameter ?? data[index].factor)}
                </Typography>
                <Typography variant='body2' fontWeight={600} color='text.primary'>
                  {typeof (data[index] as Metric).adjustedPercent === 'number'
                    ? (data[index] as Metric).adjustedPercent
                    : Math.round(((data[index] as Metric).count / count) * 100)}
                  %
                </Typography>
              </Box>
            </Grid>
          ) : null
        ))}
      </Grid>
    </Grid>
  );
}

// Utility to adjust percentages so they sum to 100, following stable sort and fair distribution
function adjustPercentages(items: { count: number }[], total: number) {
  if (total === 0) return items.map(() => 0);
  const raw = items.map((item) => (item.count / total) * 100);
  const floored = raw.map(Math.floor);
  const remainders = raw.map((val, i) => ({
    index: i,
    remainder: val - floored[i],
  }));
  const sum = floored.reduce((a, b) => a + b, 0);
  const diff = 100 - sum;

  // Stable sort: by remainder, then by original index
  remainders.sort((a, b) => {
    if (diff > 0) {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return a.index - b.index;
    }
    else {
      if (a.remainder !== b.remainder) return a.remainder - b.remainder;
      return a.index - b.index;
    }
  });

  for (let i = 0; i < Math.abs(diff); i += 1) {
    floored[remainders[i].index] += diff > 0 ? 1 : -1;
  }
  return floored;
}

function getAdjustedList(
  items: { count: number; parameter?: string; factor?: string }[],
  groupLabel: string,
  groupKey: 'parameter' | 'factor',
  maxVisible: number,
) {
  const totalCount = items.reduce((acc, curr) => acc + curr.count, 0);
  const visible = items.slice(0, maxVisible);
  const other = items.slice(maxVisible);
  let otherCount = other.reduce((acc, curr) => acc + curr.count, 0);
  while (other.length > 0) {
    const lastVisiblePct = Math.round((visible[visible.length - 1].count / totalCount) * 100);
    const otherPct = Math.round((otherCount / totalCount) * 100);
    if (otherPct <= lastVisiblePct) break;
    const toMove = other.shift();
    otherCount -= toMove?.count ?? 0;
    if (toMove) visible.push(toMove);
  }
  const list = [
    ...visible,
    ...(otherCount > 0
      ? [{
        [groupKey]: groupLabel,
        count: otherCount,
        parameters: other.reduce((acc, item) => `${acc ? `${acc}, ` : ''}${item[groupKey]}`, ''),
      }]
      : []),
  ];
  const adjustedPercents = adjustPercentages(list, totalCount);
  const listWithPercents = list.map(
    (item, i) => ({ ...item, adjustedPercent: adjustedPercents[i] }),
  );
  return { list: listWithPercents, count: totalCount };
}

type Data = {
  mood: Metric[];
  factors: MoodFactorDomain[];
  factorsLoading: boolean;
  moodsLoading: boolean;
};

interface LoggedMoodsProps {
  category?: TimeFrameCategory;
  timeFrame: string;
  data: Data;
  noData: boolean;
  onChangeTimeFrame: (v: string, category?: TimeFrameCategory) => void;
  onSelectMood: (moodId: string)=> void;
  gridSize?: number;
  numberOfLogs?: number;
}

export default function LoggedMoods({
  category,
  timeFrame: selectedTimeFrame,
  data,
  noData,
  onChangeTimeFrame: setSelectedTimeFrame,
  gridSize = 6,
  onSelectMood,
  numberOfLogs,
}: LoggedMoodsProps): JSX.Element | null {
  const [isOpen, setIsOpen] = React.useState(false);
  const [moodName, setMoodName] = React.useState('');
  const [originalFactors, setOriginalFactors] = React.useState<MoodFactorDomain[]>([]);

  React.useEffect(() => {
    setOriginalFactors((prev) => (prev.length === 0 ? data.factors : prev));
  }, [data.factors, selectedTimeFrame]);

  const onShowMore = () => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_DETAILS_VIEW_CHARTS_CLICKED, {
      location: 'progress_monitor_tab_details_view_charts',
    });
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    onSelectMood('');
    setMoodName('');
  };

  const timeFrameValues = React.useMemo(() => Object.values(TimeFrameOptions), []);
  const timeFrame = React.useMemo(
    () => timeFrameValues.findIndex((value) => value === selectedTimeFrame),
    [selectedTimeFrame, timeFrameValues],
  );

  const adjustedMoods = React.useMemo(
    () => getAdjustedList(data.mood, 'Other', 'parameter', 5),
    [data.mood],
  );

  const originalAdjustedSituations = React.useMemo(
    () => getAdjustedList(originalFactors, 'Other', 'factor', 6),
    [originalFactors],
  );

  const adjustedSituations = React.useMemo(
    () => getAdjustedList(data.factors, 'Other', 'factor', 6),
    [data.factors],
  );

  const firstSixAdjustedMoods = adjustedMoods.list.slice(0, 6);
  const firstSixAdjustedSituations = adjustedSituations.list.slice(0, 6);
  const firstSixOriginalAdjustedSituations = originalAdjustedSituations.list.slice(0, 6);

  const onMoodChange = (id: string, mood: string) => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS__MOOD_FILTER_CLICKED, {
      mood,
      id,
      location: 'progress_monitor_tab_details_view_charts',
    });

    onSelectMood(id);
    setMoodName(mood);
  };

  const handleClearFilters = () => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_DETAIL_CHART_CLEAR_FILTERS_CLICKED, {
      location: 'progress_monitor_tab_details_view_charts',
    });
    onSelectMood(''); setMoodName('');
  };

  const onTimeFrameChange = (val: string) => {
    setOriginalFactors([]);
    setSelectedTimeFrame(val, category);
  };

  return (
    <>
      <PatientDetailsCard
        key='mood-factors'
        title='Mood factors'
        selectTimeFrame={selectedTimeFrame}
        onTimeFrameChange={onTimeFrameChange}
        showViewDetails={(data.mood.length > 0 || data.factors.length > 0) && !noData}
        onViewDetails={onShowMore}
        noData={noData}
        numberOfLogs={numberOfLogs ?? 0}
        isLoading={data?.moodsLoading}
        gridProps={{
          item: true,
          xs: 12,
          md: gridSize,
        }}
      >
        <RadarChart
          data={firstSixOriginalAdjustedSituations}
          count={originalAdjustedSituations.count}
        />
      </PatientDetailsCard>
      <HLDrawer
        title='Mood details'
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: theme.zIndex.drawer + 2 }}
        maxWidth={778}
        data-testid='rewards-drawer'
      >
        <Box role='grid' display='flex' flexDirection='column'>
          <Box display='flex' alignItems='flex-start' justifyContent='space-between' my={1.5}>
            <Box display='flex' flexDirection='column' gap={0.5}>
              <Typography variant='h5' color='text.primary'>
                Moods & their factors
              </Typography>
              <Typography variant='caption' color='text.primary'>
                Click section to see which mood affected which factor
              </Typography>
              <Typography
                variant='caption'
                color={theme.custom.colors.primaryMain}
                visibility={moodName ? 'visible' : 'hidden'}
                sx={{
                  ':hover': {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  },
                }}
                onClick={handleClearFilters}
              >
                clear filters
              </Typography>
            </Box>
            <HLDropdown
              value={timeFrame}
              onChange={(e) => {
                const value = e.target.value as string | number;
                const timeFrameValue = timeFrameValues[
                  typeof value === 'number'
                    ? value
                    : timeFrameValues.indexOf(value as TimeFrameOptions)
                ];
                trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_DETAIL_DATERANGE_CLICKED, {
                  timeFrameValue,
                  clickedFrom: 'Moods and activities',
                  location: 'progress_monitor_tab_details_view_charts',
                });
                onSelectMood('');
                setMoodName('');
                onTimeFrameChange(timeFrameValue);
              }}
              options={timeFrameValues.map((val, idx) => ({ label: val, value: idx }))}
              sx={{ minWidth: 159, height: 36 }}
            />
          </Box>
          <Grid container flex={1} spacing={2}>
            <Grid item xs={12} md={6} textAlign='left'>
              <HLCard sx={{ p: 2.5, height: '100%' }}>
                <Typography variant='body1' color='text.primary' fontWeight={600}>
                  Moods
                </Typography>
                <CustomPieChart
                  data={firstSixAdjustedMoods}
                  count={adjustedMoods.count}
                  onSelectMood={onMoodChange}
                  isLoading={data?.moodsLoading}
                  noData={noData}
                />
              </HLCard>
            </Grid>
            <Grid item xs={12} md={6} textAlign='left'>
              <HLCard sx={{ p: 2.5, height: '100%' }}>
                <Typography variant='body1' color='text.primary' fontWeight={600}>
                  Factors
                  {moodName ? `: ${moodName}` : ''}
                </Typography>
                <CustomPieChart
                  data={firstSixAdjustedSituations}
                  count={adjustedSituations.count}
                  isLoading={data?.factorsLoading}
                  noData={noData}
                />
              </HLCard>
            </Grid>
          </Grid>
        </Box>
      </HLDrawer>
    </>
  );
}
